import React, { useState, useEffect, useCallback } from "react";
import {
  Clock,
  Calendar,
  LogIn,
  LogOut,
  Users,
  Settings,
  Edit2,
  Save,
  AlertTriangle,
} from "lucide-react";
import { useStaffAuth } from "../context/StaffAuthContext";
import {
  getOfficeTimePortal,
  upsertOfficeTimePortal,
  checkIn as apiCheckIn,
  checkOut as apiCheckOut,
  getTodayAttendance,
  getMyAttendance,
  getAllAttendanceForDate,
  adminMarkAttendance,
} from "../api/staffAttendancePortalApi";

import {
  Card,
  CardContent,
  Button,
  Input,
  Textarea,
  Select,
  Badge,
  Modal,
  Alert,
  Skeleton,
  EmptyState,
} from "../components/ui";

const STATUS_VARIANTS = {
  PRESENT: "success",
  ABSENT: "danger",
  LATE: "warning",
  HALF_DAY: "warning",
  HOLIDAY: "primary",
  LEAVE: "default",
};

const STATUS_OPTIONS = ["PRESENT", "ABSENT", "LATE", "HALF_DAY", "HOLIDAY", "LEAVE"];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const todayString = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatTime12hr = (time24) => {
  if (!time24) return "--";
  const [h, m] = time24.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
};

const formatMinutesToHours = (totalMinutes) => {
  const mins = Number(totalMinutes);
  if (isNaN(mins) || mins <= 0) return "—";
  const hours = Math.floor(mins / 60);
  const remainingMinutes = mins % 60;

  if (hours === 0) return `${remainingMinutes} mins`;
  if (remainingMinutes === 0) return `${hours} ${hours === 1 ? "hr" : "hrs"}`;
  return `${hours} ${hours === 1 ? "hr" : "hrs"} ${remainingMinutes} mins`;
};

const getDayName = (dateStr, opts = { weekday: "long" }) => {
  if (!dateStr) return "";
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", opts);
};

const showTimedError = (setter, message) => {
  setter(message);
  setTimeout(() => setter(""), 3000);
};

const StatusBadge = ({ status }) => (
  <Badge variant={STATUS_VARIANTS[status] || "default"} className="text-[10px] font-semibold px-2 py-0.5">
    {status ? status.replace("_", " ") : "—"}
  </Badge>
);

const StaffAttendancePortal = () => {
  const { hasPermission, isPrincipal } = useStaffAuth();
  const canManage = isPrincipal || hasPermission("STAFF_ATTENDANCE_FULL");

  // Section 1 — Today card
  const [today, setToday] = useState(null); // { attendance, dayType }
  const [todayLoading, setTodayLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [todayError, setTodayError] = useState("");
  const [officeTime, setOfficeTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchToday = useCallback(async () => {
    try {
      setTodayLoading(true);
      const data = await getTodayAttendance();
      setToday(data);
    } catch (err) {
      showTimedError(setTodayError, "Failed to load today's attendance");
    } finally {
      setTodayLoading(false);
    }
  }, []);

  const fetchOfficeTimeInfo = useCallback(async () => {
    try {
      const data = await getOfficeTimePortal();
      setOfficeTime(data?.officeTime || null);
    } catch (err) {
      // non-critical, ignore
    }
  }, []);

  useEffect(() => {
    fetchToday();
    fetchOfficeTimeInfo();
  }, [fetchToday, fetchOfficeTimeInfo]);

  const handleCheckIn = async () => {
    try {
      setActionLoading(true);
      setTodayError("");
      await apiCheckIn();
      await fetchToday();
    } catch (err) {
      showTimedError(setTodayError, err?.response?.data?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    try {
      setActionLoading(true);
      setTodayError("");
      await apiCheckOut();
      await fetchToday();
    } catch (err) {
      showTimedError(setTodayError, err?.response?.data?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  const attendance = today?.attendance;
  const dayType = today?.dayType;
  const isHoliday = dayType === "HOLIDAY" || attendance?.status === "HOLIDAY";

  // Section 2 — Monthly view
  const [monthYear, setMonthYear] = useState(new Date().getFullYear());
  const [monthMonth, setMonthMonth] = useState(new Date().getMonth() + 1);
  const [myRecords, setMyRecords] = useState([]);
  const [monthLoading, setMonthLoading] = useState(true);
  const [monthError, setMonthError] = useState("");

  const fetchMyAttendance = useCallback(async () => {
    try {
      setMonthLoading(true);
      const data = await getMyAttendance(monthYear, monthMonth);
      setMyRecords(data?.records || []);
    } catch (err) {
      showTimedError(setMonthError, "Failed to load monthly attendance");
    } finally {
      setMonthLoading(false);
    }
  }, [monthYear, monthMonth]);

  useEffect(() => {
    fetchMyAttendance();
  }, [fetchMyAttendance]);

  const monthSummary = myRecords.reduce(
    (acc, r) => {
      const key = (r.status || "").toLowerCase();
      if (key === "present") acc.present += 1;
      else if (key === "absent") acc.absent += 1;
      else if (key === "late") acc.late += 1;
      else if (key === "half_day") acc.halfDay += 1;
      else if (key === "holiday") acc.holiday += 1;
      else if (key === "leave") acc.leave += 1;
      return acc;
    },
    { present: 0, absent: 0, late: 0, halfDay: 0, holiday: 0, leave: 0 }
  );

  // Section 3 — Office time settings (canManage only)
  const [otForm, setOtForm] = useState({ startTime: "09:00", endTime: "17:00", gracePeriodMinutes: 15 });
  const [otSaving, setOtSaving] = useState(false);
  const [otError, setOtError] = useState("");
  const [otSuccess, setOtSuccess] = useState("");

  useEffect(() => {
    if (officeTime) setOtForm(officeTime);
  }, [officeTime]);

  const handleOfficeTimeSave = async (e) => {
    e.preventDefault();
    try {
      setOtSaving(true);
      await upsertOfficeTimePortal({
        startTime: otForm.startTime,
        endTime: otForm.endTime,
        gracePeriodMinutes: Number(otForm.gracePeriodMinutes),
      });
      setOtSuccess("Office time updated");
      setTimeout(() => setOtSuccess(""), 3000);
      fetchOfficeTimeInfo();
    } catch (err) {
      showTimedError(setOtError, "Failed to save office time");
    } finally {
      setOtSaving(false);
    }
  };

  // Section 4 — View other staff (canManage only)
  const [otherDate, setOtherDate] = useState(todayString());
  const [otherRecords, setOtherRecords] = useState([]);
  const [otherDayType, setOtherDayType] = useState("");
  const [otherLoading, setOtherLoading] = useState(true);
  const [otherError, setOtherError] = useState("");

  const fetchOthers = useCallback(async () => {
    try {
      setOtherLoading(true);
      const data = await getAllAttendanceForDate(otherDate);
      setOtherRecords(data?.staffAttendance || []);
      setOtherDayType(data?.dayType || "");
    } catch (err) {
      showTimedError(setOtherError, "Failed to load staff attendance");
    } finally {
      setOtherLoading(false);
    }
  }, [otherDate]);

  useEffect(() => {
    if (canManage) fetchOthers();
  }, [canManage, fetchOthers]);

  // Admin mark modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLocked, setModalLocked] = useState(false);
  const [modalForm, setModalForm] = useState({
    staffId: "",
    staffName: "",
    date: "",
    status: "PRESENT",
    checkInTime: "",
    checkOutTime: "",
    remarks: "",
  });
  const [modalSaving, setModalSaving] = useState(false);
  const [modalError, setModalError] = useState("");

  const openMarkModalForRow = (item) => {
    setModalError("");
    setModalLocked(true);
    setModalForm({
      staffId: item.staff?._id || "",
      staffName: item.staff?.name || "",
      date: otherDate,
      status: item.attendance?.status || "PRESENT",
      checkInTime: item.attendance?.checkInTime || "",
      checkOutTime: item.attendance?.checkOutTime || "",
      remarks: item.attendance?.remarks || "",
    });
    setModalOpen(true);
  };

  const openMarkModalGeneric = async () => {
    setModalError("");
    if (otherRecords.length === 0) {
      await fetchOthers();
    }
    setModalLocked(false);
    setModalForm({
      staffId: "",
      staffName: "",
      date: otherDate || todayString(),
      status: "PRESENT",
      checkInTime: "",
      checkOutTime: "",
      remarks: "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalError("");
  };

  const handleModalSave = async () => {
    if (!modalForm.staffId || !modalForm.date) {
      setModalError("Staff and date are required");
      return;
    }
    try {
      setModalSaving(true);
      setModalError("");
      const { staffName, ...payload } = modalForm;
      await adminMarkAttendance(payload);
      setModalOpen(false);
      fetchOthers();
      fetchMyAttendance();
      fetchToday();
    } catch (err) {
      setModalError("Failed to save attendance record");
    } finally {
      setModalSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 antialiased">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800 pb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
            Attendance Portal
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            Check in, check out, and track your attendance
          </p>
        </div>
      </div>

      {/* Section 1: Today Card */}
      {todayError && <Alert variant="danger">{todayError}</Alert>}

      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
        <CardContent className="p-4 sm:p-6">
          {todayLoading ? (
            <Skeleton height={80} width="100%" />
          ) : (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="space-y-1">
                <span className="text-xs font-semibold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                  {getDayName(todayString())}
                </span>
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">{todayString()}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                  {currentTime.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </p>
                {officeTime && (
                  <p className="text-xs text-neutral-400 dark:text-neutral-500 pt-1">
                    Office hours: {formatTime12hr(officeTime.startTime)} – {formatTime12hr(officeTime.endTime)}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-start md:items-end gap-3">
                {isHoliday ? (
                  <Badge variant="primary" className="text-xs px-3 py-1.5 flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    Today is a holiday
                  </Badge>
                ) : !attendance?.checkInTime ? (
                  <Button
                    variant="primary"
                    size="md"
                    isLoading={actionLoading}
                    onClick={handleCheckIn}
                    leftIcon={<LogIn className="w-4 h-4" />}
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold px-6 py-2.5"
                  >
                    {actionLoading ? "Checking in…" : "Check In"}
                  </Button>
                ) : !attendance?.checkOutTime ? (
                  <div className="flex flex-col items-start md:items-end gap-2">
                    <p className="text-xs text-neutral-600 dark:text-neutral-300">
                      Checked in at <span className="font-semibold text-neutral-900 dark:text-neutral-100">{formatTime12hr(attendance.checkInTime)}</span>
                    </p>
                    {attendance.isLate && (
                      <Alert variant="warning" className="py-1 px-2.5 text-xs flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        You are {formatMinutesToHours(attendance.lateByMinutes)} late
                      </Alert>
                    )}
                    <Button
                      variant="danger"
                      size="md"
                      isLoading={actionLoading}
                      onClick={handleCheckOut}
                      leftIcon={<LogOut className="w-4 h-4" />}
                      className="text-xs font-semibold px-6 py-2.5"
                    >
                      {actionLoading ? "Checking out…" : "Check Out"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-start md:items-end gap-2">
                    <p className="text-xs text-neutral-600 dark:text-neutral-300">
                      In: <span className="font-semibold text-neutral-900 dark:text-neutral-100">{formatTime12hr(attendance.checkInTime)}</span> · Out:{" "}
                      <span className="font-semibold text-neutral-900 dark:text-neutral-100">{formatTime12hr(attendance.checkOutTime)}</span>
                    </p>
                    <StatusBadge status={attendance.status} />
                    {attendance.isLate && (
                      <Alert variant="warning" className="py-1 px-2.5 text-xs flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        You were {formatMinutesToHours(attendance.lateByMinutes)} late
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 2: My Attendance */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
            My Attendance
          </h2>
          {canManage && (
            <Button
              variant="primary"
              size="xs"
              onClick={openMarkModalGeneric}
              leftIcon={<Edit2 className="w-3.5 h-3.5" />}
              className="text-xs font-semibold py-1.5"
            >
              Mark Attendance
            </Button>
          )}
        </div>

        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
          <CardContent className="p-4 sm:p-6 space-y-5">
            <div className="flex items-center gap-2">
              <Select
                value={monthMonth}
                onChange={(e) => setMonthMonth(Number(e.target.value))}
                className="text-xs"
              >
                {MONTH_NAMES.map((m, idx) => (
                  <option key={m} value={idx + 1}>
                    {m}
                  </option>
                ))}
              </Select>
              <Select
                value={monthYear}
                onChange={(e) => setMonthYear(Number(e.target.value))}
                className="text-xs"
              >
                {[monthYear - 1, monthYear, monthYear + 1].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                ["Present", monthSummary.present, "bg-emerald-50/60 border-emerald-100 text-emerald-800"],
                ["Absent", monthSummary.absent, "bg-rose-50/60 border-rose-100 text-rose-800"],
                ["Late", monthSummary.late, "bg-amber-50/60 border-amber-100 text-amber-800"],
                ["Half Day", monthSummary.halfDay, "bg-orange-50/60 border-orange-100 text-orange-800"],
                ["Holiday", monthSummary.holiday, "bg-sky-50/60 border-sky-100 text-sky-800"],
                ["Leave", monthSummary.leave, "bg-purple-50/60 border-purple-100 text-purple-800"],
              ].map(([label, value, cls]) => (
                <div key={label} className={`rounded-xl border p-3 text-center ${cls}`}>
                  <p className="text-xl font-bold tracking-tight">{value}</p>
                  <p className="text-[11px] font-medium opacity-80">{label}</p>
                </div>
              ))}
            </div>

            {monthError && <Alert variant="danger">{monthError}</Alert>}

            {monthLoading ? (
              <div className="space-y-2 py-4">
                <Skeleton height={40} width="100%" />
                <Skeleton height={40} width="100%" />
              </div>
            ) : myRecords.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No attendance records found"
                description="No log history found for the selected month."
              />
            ) : (
              <div className="overflow-x-auto border border-neutral-200 dark:border-neutral-800 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-neutral-50/80 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Day</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Check In</th>
                      <th className="py-3 px-4">Check Out</th>
                      <th className="py-3 px-4">Late By</th>
                      <th className="py-3 px-4">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200">
                    {myRecords.map((r) => (
                      <tr key={r._id} className="hover:bg-neutral-50/80 transition-colors">
                        <td className="py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">{r.date}</td>
                        <td className="py-3 px-4 text-neutral-500 dark:text-neutral-400">{getDayName(r.date, { weekday: "short" })}</td>
                        <td className="py-3 px-4">
                          <StatusBadge status={r.status} />
                        </td>
                        <td className="py-3 px-4">{formatTime12hr(r.checkInTime)}</td>
                        <td className="py-3 px-4">{formatTime12hr(r.checkOutTime)}</td>
                        <td className="py-3 px-4">{formatMinutesToHours(r.lateByMinutes)}</td>
                        <td className="py-3 px-4 max-w-[160px] truncate text-neutral-500 dark:text-neutral-400">{r.remarks || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Office Time Settings (canManage) */}
      {canManage && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
            <Settings className="w-3.5 h-3.5" />
            Office Time Settings
          </h2>

          {otError && <Alert variant="danger">{otError}</Alert>}
          {otSuccess && <Alert variant="success">{otSuccess}</Alert>}

          <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
            <CardContent className="p-4 sm:p-6">
              <form onSubmit={handleOfficeTimeSave} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                <Input
                  label="Start Time"
                  type="time"
                  value={otForm.startTime}
                  onChange={(e) => setOtForm({ ...otForm, startTime: e.target.value })}
                  required
                />
                <Input
                  label="End Time"
                  type="time"
                  value={otForm.endTime}
                  onChange={(e) => setOtForm({ ...otForm, endTime: e.target.value })}
                  required
                />
                <Input
                  label="Grace Period (minutes)"
                  type="number"
                  min="0"
                  value={otForm.gracePeriodMinutes}
                  onChange={(e) => setOtForm({ ...otForm, gracePeriodMinutes: e.target.value })}
                  required
                />
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={otSaving}
                  className="w-full text-xs font-semibold py-2.5"
                >
                  {otSaving ? "Saving…" : "Save Settings"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section 4: All Staff Attendance (canManage) */}
      {canManage && (
        <div className="space-y-3">
          <h2 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            All Staff Attendance
          </h2>

          <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
            <CardContent className="p-4 sm:p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-full sm:w-auto">
                  <Input
                    type="date"
                    value={otherDate}
                    onChange={(e) => setOtherDate(e.target.value)}
                    leftIcon={<Calendar className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
                    className="text-xs"
                  />
                </div>
                {otherDayType && (
                  <Badge variant="default" className="text-xs font-normal">
                    Day type: {otherDayType}
                  </Badge>
                )}
              </div>

              {otherError && <Alert variant="danger">{otherError}</Alert>}

              {otherLoading ? (
                <div className="space-y-2 py-4">
                  <Skeleton height={40} width="100%" />
                  <Skeleton height={40} width="100%" />
                </div>
              ) : otherRecords.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No records found"
                  description="No staff attendance records exist for the selected date."
                />
              ) : (
                <div className="overflow-x-auto border border-neutral-200 dark:border-neutral-800 rounded-xl">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-neutral-50/80 border-b border-neutral-200 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="py-3 px-4">Staff Name</th>
                        <th className="py-3 px-4">Staff Code</th>
                        <th className="py-3 px-4">Check In</th>
                        <th className="py-3 px-4">Check Out</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4">Late By</th>
                        <th className="py-3 px-4">Remarks</th>
                        <th className="py-3 px-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800 text-neutral-700 dark:text-neutral-200">
                      {otherRecords.map((item) => (
                        <tr
                          key={item.staff?._id}
                          className="hover:bg-neutral-50/80 transition-colors cursor-pointer"
                          onClick={() => openMarkModalForRow(item)}
                        >
                          <td className="py-3 px-4 font-semibold text-neutral-900 dark:text-neutral-100">{item.staff?.name || "—"}</td>
                          <td className="py-3 px-4 text-neutral-500 dark:text-neutral-400">{item.staff?.staffCode || "—"}</td>
                          <td className="py-3 px-4">{formatTime12hr(item.attendance?.checkInTime)}</td>
                          <td className="py-3 px-4">{formatTime12hr(item.attendance?.checkOutTime)}</td>
                          <td className="py-3 px-4">
                            <StatusBadge status={item.attendance?.status} />
                          </td>
                          <td className="py-3 px-4">
                            {formatMinutesToHours(item.attendance?.lateByMinutes)}
                          </td>
                          <td className="py-3 px-4 max-w-[160px] truncate text-neutral-500 dark:text-neutral-400">
                            {item.attendance?.remarks || "—"}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button variant="ghost" size="xs" className="p-1 h-7 w-7">
                              <Edit2 className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin Mark Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title="Mark Attendance"
        size="md"
      >
        <div className="space-y-4 py-1">
          {modalError && <Alert variant="danger">{modalError}</Alert>}

          <div>
            <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Staff Member
            </label>
            {modalLocked ? (
              <div className="w-full border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-xs bg-neutral-50 dark:bg-neutral-950 text-neutral-700 dark:text-neutral-200 font-semibold">
                {modalForm.staffName || "—"}
              </div>
            ) : (
              <Select
                value={modalForm.staffId}
                onChange={(e) => {
                  const selected = otherRecords.find((item) => item.staff?._id === e.target.value);
                  setModalForm({
                    ...modalForm,
                    staffId: e.target.value,
                    staffName: selected?.staff?.name || "",
                  });
                }}
              >
                <option value="">Select staff…</option>
                {otherRecords.map((item) => (
                  <option key={item.staff?._id} value={item.staff?._id}>
                    {item.staff?.name} ({item.staff?.staffCode})
                  </option>
                ))}
              </Select>
            )}
          </div>

          <Input
            label="Date"
            type="date"
            value={modalForm.date}
            onChange={(e) => setModalForm({ ...modalForm, date: e.target.value })}
          />

          <Select
            label="Status"
            value={modalForm.status}
            onChange={(e) => setModalForm({ ...modalForm, status: e.target.value })}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </Select>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Check In"
              type="time"
              value={modalForm.checkInTime}
              onChange={(e) => setModalForm({ ...modalForm, checkInTime: e.target.value })}
            />
            <Input
              label="Check Out"
              type="time"
              value={modalForm.checkOutTime}
              onChange={(e) => setModalForm({ ...modalForm, checkOutTime: e.target.value })}
            />
          </div>

          <Textarea
            label="Remarks"
            value={modalForm.remarks}
            onChange={(e) => setModalForm({ ...modalForm, remarks: e.target.value })}
            rows={3}
            placeholder="Optional remarks..."
          />

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800">
            <Button type="button" variant="ghost" size="sm" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={modalSaving}
              onClick={handleModalSave}
              leftIcon={<Save className="w-3.5 h-3.5" />}
            >
              Save Record
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffAttendancePortal;